import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronDown } from 'react-icons/fa6';
import { ReactComponent as Logo } from '../assets/logo-light.svg';
import { useAuth } from '../lib/auth';

function initials(user) {
  if (!user) return '?';
  if (user.first_name) return user.first_name[0].toUpperCase();
  if (user.email) return user.email[0].toUpperCase();
  return '?';
}

function PlayerUserMenu({ user, signOut }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        className="inline-flex items-center gap-2 text-sm font-medium text-slate2 hover:text-ink transition-colors"
      >
        <span className="w-8 h-8 rounded-full bg-brand-deep/15 text-brand-deep flex items-center justify-center text-xs font-display font-semibold select-none">
          {initials(user)}
        </span>
        <span className="hidden sm:block max-w-[120px] truncate text-[13px]">
          {user.first_name || user.email?.split('@')[0]}
        </span>
        <FaChevronDown className={`text-[9px] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl border border-ink/10 shadow-[0_8px_24px_-8px_rgba(15,42,46,0.18)] py-1.5 z-50">
          <div className="px-4 py-2.5 border-b border-ink/5 mb-1">
            <p className="text-[11px] tracking-[0.14em] uppercase text-brand-aubergine">Signed in as</p>
            <p className="text-sm text-ink truncate mt-0.5">{user.email}</p>
          </div>
          <Link
            to="/my-courses"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-ink hover:bg-cream/80 transition-colors"
          >
            My courses
          </Link>
          {user.is_admin && (
            <Link
              to="/admin"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-ink hover:bg-cream/80 transition-colors"
            >
              Admin
            </Link>
          )}
          <div className="border-t border-ink/5 mt-1 pt-1">
            <button
              type="button"
              onClick={() => { setOpen(false); signOut(); }}
              className="block w-full text-left px-4 py-2 text-sm text-slate2 hover:text-ink hover:bg-cream/80 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CoursePlayerHeader({
  courseTitle,
  courseHref,
  progress,
}) {
  const { user, loading, signOut } = useAuth();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 bg-bone print:hidden transition-shadow ${
        scrolled ? 'shadow-[0_1px_0_rgba(15,42,46,0.12)]' : 'border-b border-ink/10'
      }`}
    >
      <div className="container-page flex items-center justify-between gap-4 h-14">
        <Link
          to={courseHref || '/my-courses'}
          className="inline-flex items-center gap-2.5 min-w-0 group"
        >
          <Logo
            aria-label="Learning Outcomes Academy"
            className="h-8 w-auto flex-shrink-0 text-[#919091]"
          />
          <span className="text-ink/20 text-sm flex-shrink-0">|</span>
          <span className="text-sm font-medium text-slate2 group-hover:text-ink transition-colors truncate">
            {courseTitle}
          </span>
        </Link>

        <div className="flex items-center gap-5">
          {progress && (
            <span className="hidden sm:block text-xs text-slate2 whitespace-nowrap">
              {progress}
            </span>
          )}
          {!loading && (
            user ? (
              <PlayerUserMenu user={user} signOut={signOut} />
            ) : (
              <Link
                to="/login"
                className="text-sm font-medium text-slate2 hover:text-ink transition-colors"
              >
                Sign in
              </Link>
            )
          )}
        </div>
      </div>
    </header>
  );
}
